import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

function QuoteSectionEditorModal({ isOpen, onClose, onSave, initialLineItems, initialSectionName, initialSectionDescription }) {
    // State for the section name and description, initialized from props
    const [sectionName, setSectionName] = useState(initialSectionName || '');
    const [sectionDescription, setSectionDescription] = useState(initialSectionDescription || '');
    
    // State for line items, performing a deep copy
    const [lineItems, setLineItems] = useState([]);
    // New state to manage which item's details are expanded on mobile
    const [expandedItemId, setExpandedItemId] = useState(null);

    // Define common unit types
    const commonUnitTypes = [
        'sq ft', 'linear ft', 'unit', 'per hour'
    ];

    useEffect(() => {
        if (isOpen) {
            setSectionName(initialSectionName || '');
            setSectionDescription(initialSectionDescription || '');
            // When modal opens, reset all items to be collapsed
            setExpandedItemId(null); 
            setLineItems(JSON.parse(JSON.stringify(initialLineItems || [])).map(item => ({
                ...item,
                unitPrice: parseFloat(item.unitPrice) || 0,
                quantity: parseFloat(item.quantity) || 0,
                isTaxable: item.isTaxable !== undefined ? !!item.isTaxable : true,
                // Description is no longer part of the item model
            })));
        }
    }, [initialLineItems, initialSectionName, initialSectionDescription, isOpen]);

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline'],
            [{'list': 'ordered'}, {'list': 'bullet'}],
            ['clean']
        ],
    };

    if (!isOpen) return null;

    const calculateSectionSubtotal = (items) => {
        return items.reduce((acc, item) => acc + (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0), 0);
    };

    const handleItemChange = (id, field, value) => {
        setLineItems(prevItems =>
            prevItems.map(item => {
                if (item.id === id) {
                    const isNumeric = field === 'unitPrice' || field === 'quantity';
                    const finalValue = isNumeric ? parseFloat(value) || 0 : value;
                    return { ...item, [field]: finalValue };
                }
                return item;
            })
        );
    };
    
    const handleTaxableToggle = (id) => {
        setLineItems(prevItems =>
            prevItems.map(item =>
                item.id === id ? { ...item, isTaxable: !item.isTaxable } : item
            )
        );
    };

    const handleRemoveItem = (id) => {
        setLineItems(prevItems => prevItems.filter(item => item.id !== id));
    };

    // Handler to toggle the expanded details view on mobile
    const handleToggleExpand = (id) => {
        setExpandedItemId(prevId => (prevId === id ? null : id));
    };

    const handleAddCustomItem = () => {
        setLineItems(prevItems => {
            const newId = Date.now();
            const newSortOrder = prevItems.length > 0 ? Math.max(...prevItems.map(item => item.sortOrder || 0)) + 1 : 0;
            return [
                ...prevItems,
                // New items no longer include a 'description' field
                { id: newId, itemName: '', unitType: 'unit', unitPrice: 0, quantity: 1, isTaxable: true, sortOrder: newSortOrder },
            ];
        });
    };

    const handleSave = () => {
        // The data in lineItems is the single source of truth, so saving is unaffected by UI visibility
        onSave({ name: sectionName, description: sectionDescription, items: lineItems });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <h2 className="text-3xl font-bold text-primary-700">Edit Section</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            aria-label="Close modal"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </div>
                    <div className="mt-4 space-y-4">
                        <div>
                            <label htmlFor="sectionName" className="block text-sm font-medium text-slate-700">Section Name</label>
                            <input
                                type="text"
                                id="sectionName"
                                value={sectionName}
                                onChange={(e) => setSectionName(e.target.value)}
                                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                                placeholder="e.g., Living Room Repaint"
                            />
                        </div>
                         <div>
                          <label htmlFor="sectionDescription" className="block text-sm font-medium text-slate-700">Section Description</label>
                          
                          {/* This wrapper div creates the styled container.
                            - rounded-md, shadow-sm, border: Matches your other inputs.
                            - overflow-hidden: Crucial for making the rounded corners apply to the Quill editor inside.
                            - focus-within:*: A Tailwind trick that applies the focus styles when anything inside this div is focused.
                          */}
                          <div className="mt-1 rounded-md shadow-sm border border-gray-300 focus-within:ring-1 focus-within:ring-primary-500 focus-within:border-primary-500 overflow-hidden">
                              <ReactQuill 
                                  theme="snow" 
                                  value={sectionDescription} 
                                  onChange={setSectionDescription}
                                  modules={quillModules}
                              />
                          </div>
                      </div>
                    </div>
                </div>

                <div className="p-6">
                    {lineItems.length === 0 ? (
                        <p className="text-gray-500 text-center py-4 border border-dashed border-gray-300 rounded-lg mb-4">
                            No items in this section. Click "Add Custom Item" below to add one.
                        </p>
                    ) : (
                        <div className="w-full">
                            {/* Desktop Header - Hidden on mobile */}
                            <div className="hidden md:grid md:grid-cols-[minmax(0,3fr)_repeat(3,minmax(0,1fr))_auto_minmax(0,1fr)_auto] gap-4 px-3 py-3 bg-blue-50 rounded-t-lg border-b border-gray-200">
                                <div className="text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Item Name</div>
                                <div className="text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Unit Type</div>
                                <div className="text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Unit Price</div>
                                <div className="text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Qty</div>
                                <div className="text-center text-xs font-medium text-blue-700 uppercase tracking-wider">Taxable</div>
                                <div className="text-right text-xs font-medium text-blue-700 uppercase tracking-wider">Total</div>
                                <div className="w-12"></div> {/* Spacer for actions column */}
                            </div>

                            {/* Line Items List */}
                            <div className="border border-gray-200 rounded-b-lg md:border-t-0 md:rounded-t-none">
                                {lineItems.map(item => (
                                    <div key={item.id} className="border-b border-gray-200 last:border-b-0">
                                        {/* Main Row - Responsive Grid */}
                                        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 p-3 md:grid-cols-[minmax(0,3fr)_repeat(3,minmax(0,1fr))_auto_minmax(0,1fr)_auto] md:gap-4">
                                            
                                            {/* Item Name: Text on mobile, Input on desktop */}
                                            <div className="font-semibold text-slate-800 md:hidden">{item.itemName}</div>
                                            <div className="hidden md:block">
                                                <input type="text" value={item.itemName} onChange={(e) => handleItemChange(item.id, 'itemName', e.target.value)} className="block w-full p-2 border border-gray-300 rounded-md text-sm" placeholder="Item Name" required />
                                            </div>

                                            {/* Unit Type: Hidden on mobile */}
                                            <div className="hidden md:block">
                                                <select value={item.unitType} onChange={(e) => handleItemChange(item.id, 'unitType', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white" required>
                                                    {commonUnitTypes.map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            {/* Unit Price: Hidden on mobile */}
                                            <div className="hidden md:block"><input type="number" value={item.unitPrice} onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm text-right" min="0" step="0.01" required /></div>

                                            {/* Quantity: Always visible */}
                                            <div><input type="number" value={item.quantity} onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)} className="w-24 p-2 border border-gray-300 rounded-md text-sm" min="0" required /></div>

                                            {/* Taxable: Hidden on mobile */}
                                            <div className="hidden md:flex justify-center"><input type="checkbox" checked={!!item.isTaxable} onChange={() => handleTaxableToggle(item.id)} className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" /></div>
                                            
                                            {/* Total: Hidden on mobile */}
                                            <div className="hidden md:block text-right text-sm font-semibold text-gray-900">${((parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}</div>

                                            {/* Actions: Expand on mobile, just Remove on desktop */}
                                            <div className="flex items-center justify-end space-x-2 w-12">
                                                <button type="button" onClick={() => handleToggleExpand(item.id)} className="text-gray-500 hover:text-primary-600 p-1 rounded-full hover:bg-gray-100 md:hidden" aria-label="Edit details">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path></svg>
                                                </button>
                                                <button type="button" onClick={() => handleRemoveItem(item.id)} className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50" aria-label="Remove item">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expandable Details Drawer - Mobile Only */}
                                        {expandedItemId === item.id && (
                                            <div className="p-4 bg-slate-50 border-t border-gray-200 md:hidden">
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-slate-606 mb-1">Item Name</label>
                                                        <input type="text" value={item.itemName} onChange={(e) => handleItemChange(item.id, 'itemName', e.target.value)} className="block w-full p-2 border border-gray-300 rounded-md text-sm" required />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-606 mb-1">Unit Price</label>
                                                            <input type="number" value={item.unitPrice} onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm text-right" min="0" step="0.01" required />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium text-slate-606 mb-1">Unit Type</label>
                                                            <select value={item.unitType} onChange={(e) => handleItemChange(item.id, 'unitType', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white" required>
                                                                {commonUnitTypes.map(type => (
                                                                    <option key={type} value={type}>{type}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <input id={`taxable-${item.id}`} type="checkbox" checked={!!item.isTaxable} onChange={() => handleTaxableToggle(item.id)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                                                        <label htmlFor={`taxable-${item.id}`} className="ml-2 block text-sm text-slate-900">Is this item taxable?</label>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                        <button type="button" onClick={handleAddCustomItem} className="px-3 py-2 md:px-4 md:py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 w-full sm:w-auto">
                            + Add Custom Item
                        </button>
                        <div className="flex items-center justify-between w-full sm:w-auto space-x-4">
                            <span className="text-base md:text-lg font-semibold text-gray-800">
                                Total: ${calculateSectionSubtotal(lineItems).toFixed(2)}
                            </span>
                            <button onClick={handleSave} className="px-3 py-2 md:px-6 md:py-3 bg-green-600 text-white font-medium rounded-lg shadow-md hover:bg-green-700 w-full sm:w-auto">
                                Save Section & Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuoteSectionEditorModal;
